import React, { useMemo, useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Percent, Search, FileDown, Save, RotateCcw, Loader2, RefreshCw, ArrowRight, AlertCircle } from "lucide-react";
import useCountryTransferts from "../../../../hooks/administration/countryTransferts/useCountryTransferts";
import useToken from "../../../../hooks/auth/useToken";
import { countryTransfertService } from "../../../../services/administration/countryTransfertService";
import { CountryTransfert } from "../../../../types/features/administration/CountryTransfert";
import { alertError, alertSuccess, alertWarning, getErrorMessage } from "../../../../helpers/alertError";
import { countryNameFr, formatNumberFr, groupRatesBySender, normalizeRateInput } from "../../../../utils/dailyRates";
import { downloadDailyRatesPdf } from "../../../../utils/downloadDailyRatesPdf";

const formatDateTime = (value?: string) => {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd MMM yyyy à HH:mm", { locale: fr });
  } catch {
    return value;
  }
};

const DailyRatesPage: React.FC = () => {
  const { countryTransferts, countryTransfertsLoading, getCountryTransferts } = useCountryTransferts({});
  const { token } = useToken();

  // Valeurs saisies, indexées par id de corridor (seulement celles modifiées)
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [search, setSearch] = useState("");
  const [senderFilter, setSenderFilter] = useState<string>("all");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  const allGroups = useMemo(() => groupRatesBySender(countryTransferts || []), [countryTransferts]);

  const lastUpdate = useMemo(() => {
    const dates = (countryTransferts || []).map((t) => t.updated_at).filter(Boolean) as string[];
    if (!dates.length) return undefined;
    return dates.reduce((max, d) => (new Date(d) > new Date(max) ? d : max));
  }, [countryTransferts]);

  const groups = useMemo(() => {
    const s = search.trim().toLowerCase();
    return allGroups
      .filter((g) => senderFilter === "all" || String(g.senderId) === senderFilter)
      .map((g) => ({
        ...g,
        items: g.items.filter((t) => {
          if (!s) return true;
          return [
            countryNameFr(t.country_sender),
            countryNameFr(t.country_destination),
            t.country_sender?.devise,
            t.country_destination?.devise,
          ]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(s));
        }),
      }))
      .filter((g) => g.items.length > 0);
  }, [allGroups, search, senderFilter]);

  // Corridors réellement modifiés (valeur différente de celle enregistrée)
  const changedIds = useMemo(() => {
    return Object.keys(drafts).filter((id) => {
      const original = countryTransferts.find((t) => String(t.id) === id);
      if (!original) return false;
      const normalized = normalizeRateInput(drafts[id]);
      return normalized === null || Number(normalized) !== Number(original.rate);
    });
  }, [drafts, countryTransferts]);

  const invalidIds = changedIds.filter((id) => normalizeRateInput(drafts[id]) === null);

  const handleChange = (t: CountryTransfert, value: string) => {
    const id = String(t.id);
    setDrafts((prev) => ({ ...prev, [id]: value }));
    if (rowErrors[id]) setRowErrors(({ [id]: _removed, ...rest }) => rest);
  };

  const resetRow = (id: string) => {
    setDrafts(({ [id]: _removed, ...rest }) => rest);
    setRowErrors(({ [id]: _removed, ...rest }) => rest);
  };

  const resetAll = () => {
    setDrafts({});
    setRowErrors({});
  };

  const handleSave = async () => {
    if (!changedIds.length) return;
    if (invalidIds.length) {
      alertWarning("Certains taux saisis sont invalides. Utilisez un nombre positif, par exemple 426 ou 0,0025.");
      return;
    }

    setSaving(true);
    const results = await Promise.allSettled(
      changedIds.map((id) =>
        countryTransfertService.updateCountryTransfert(token, id, { rate: normalizeRateInput(drafts[id])! })
      )
    );

    const failed: Record<string, string> = {};
    results.forEach((res, i) => {
      if (res.status === "rejected") failed[changedIds[i]] = getErrorMessage(res.reason, "Échec de la mise à jour");
    });
    const successCount = results.length - Object.keys(failed).length;

    // On ne garde en brouillon que les lignes en échec
    setDrafts((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => failed[id])));
    setRowErrors(failed);
    await getCountryTransferts();
    setSaving(false);

    if (!Object.keys(failed).length) {
      alertSuccess(`${successCount} taux mis à jour avec succès.`);
    } else if (successCount > 0) {
      alertWarning(
        `${successCount} taux mis à jour, ${Object.keys(failed).length} en échec. Vérifiez les lignes en rouge.`
      );
    } else {
      alertError("Aucun taux n'a pu être mis à jour. Vérifiez les lignes en rouge.");
    }
  };

  const handleDownload = async () => {
    if (changedIds.length) {
      alertWarning(
        "Vous avez des modifications non enregistrées. Enregistrez-les d'abord : le PDF est généré à partir des taux enregistrés."
      );
      return;
    }
    setDownloading(true);
    try {
      // Données fraîches au moment du téléchargement
      const fresh = await countryTransfertService.getCountryTransferts(500, 0);
      await downloadDailyRatesPdf(fresh);
    } catch (error) {
      console.error(error);
      alertError(getErrorMessage(error, "Impossible de générer le PDF des taux."));
    } finally {
      setDownloading(false);
    }
  };

  if (countryTransfertsLoading && !countryTransferts.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des taux...</p>
        </div>
      </div>
    );
  }

  let counter = 0;

  return (
    <div className="p-2 space-y-6">
      {/* En-tête */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Percent className="w-8 h-8 mt-1 text-green-600" />
            Taux du jour
          </h1>
          <p className="text-gray-600 mt-1">
            Mettez à jour les taux d'envoi par corridor et téléchargez le tableau quotidien.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-60"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            <span>{downloading ? "Génération..." : "Télécharger le PDF"}</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving || changedIds.length === 0}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span>Enregistrer{changedIds.length > 0 ? ` (${changedIds.length})` : ""}</span>
          </button>
        </div>
      </div>

      {/* Résumé */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <p className="text-sm text-gray-500">Corridors</p>
          <p className="text-3xl font-bold text-green-600">{countryTransferts.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <p className="text-sm text-gray-500">Pays de départ</p>
          <p className="text-3xl font-bold text-blue-600">{allGroups.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <p className="text-sm text-gray-500">Dernière mise à jour</p>
          <p className="text-lg font-semibold text-gray-800 mt-2">{formatDateTime(lastUpdate)}</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un pays ou une devise..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <select
          value={senderFilter}
          onChange={(e) => setSenderFilter(e.target.value)}
          className="py-2 pl-3 pr-8 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          <option value="all">Tous les pays de départ</option>
          {allGroups.map((g) => (
            <option key={g.senderId} value={String(g.senderId)}>
              {countryNameFr(g.sender)}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-2 md:ml-auto">
          {changedIds.length > 0 && (
            <button
              onClick={resetAll}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
            >
              <RotateCcw className="w-4 h-4" />
              Annuler les modifications
            </button>
          )}
          <button
            onClick={() => getCountryTransferts()}
            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
            title="Actualiser"
          >
            <RefreshCw className={`w-4 h-4 ${countryTransfertsLoading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0B3A6E] text-white">
              <tr>
                <th className="px-4 py-3 text-left font-semibold w-14">N°</th>
                <th className="px-4 py-3 text-left font-semibold">Corridor</th>
                <th className="px-4 py-3 text-left font-semibold">Taux d'envoi</th>
                <th className="px-4 py-3 text-left font-semibold">Dernière modification</th>
                <th className="px-4 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-gray-500">
                    Aucun corridor trouvé
                  </td>
                </tr>
              )}

              {groups.map((group) => (
                <React.Fragment key={group.senderId}>
                  <tr className="bg-gray-100 border-t border-gray-200">
                    <td colSpan={5} className="px-4 py-2 font-semibold text-gray-800">
                      <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2 align-middle" />
                      {countryNameFr(group.sender)}
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        {group.sender?.devise} · {group.items.length} corridor(s)
                      </span>
                    </td>
                  </tr>

                  {group.items.map((t) => {
                    counter += 1;
                    const id = String(t.id);
                    const value = drafts[id] ?? formatNumberFr(t.rate).replace(/ /g, "");
                    const isChanged = changedIds.includes(id);
                    const isInvalid = isChanged && normalizeRateInput(drafts[id]) === null;
                    const error = rowErrors[id];
                    const preview = normalizeRateInput(value);

                    return (
                      <tr
                        key={id}
                        className={`border-t border-gray-100 ${
                          error ? "bg-red-50" : isChanged ? "bg-amber-50" : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-3 text-gray-500">{counter}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 font-medium text-gray-800">
                            {countryNameFr(t.country_sender)}
                            <ArrowRight className="w-4 h-4 text-gray-400" />
                            {countryNameFr(t.country_destination)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {t.country_sender?.devise} → {t.country_destination?.devise}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 whitespace-nowrap">1 {t.country_sender?.devise} =</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={value}
                              onChange={(e) => handleChange(t, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleSave();
                                if (e.key === "Escape") resetRow(id);
                              }}
                              className={`w-36 px-3 py-1.5 border rounded-lg text-right font-mono focus:outline-none focus:ring-2 ${
                                isInvalid || error
                                  ? "border-red-400 focus:ring-red-400"
                                  : isChanged
                                    ? "border-amber-400 focus:ring-amber-400"
                                    : "border-gray-200 focus:ring-green-500"
                              }`}
                            />
                            <span className="text-gray-500">{t.country_destination?.devise}</span>
                          </div>
                          {isChanged && !isInvalid && preview && (
                            <p className="text-xs text-amber-700 mt-1">
                              Ancien : {formatNumberFr(t.rate)} → Nouveau : {formatNumberFr(preview)}
                            </p>
                          )}
                          {isInvalid && <p className="text-xs text-red-600 mt-1">Taux invalide</p>}
                          {error && (
                            <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> {error}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDateTime(t.updated_at)}</td>
                        <td className="px-4 py-3">
                          {(isChanged || error) && (
                            <button
                              onClick={() => resetRow(id)}
                              className="p-1 text-gray-400 hover:text-gray-700"
                              title="Annuler la modification"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DailyRatesPage;
